import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Film, Newspaper, Inbox, ExternalLink } from "lucide-react";
import api, { formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/components/Motion";
import SEO from "@/components/SEO";
import { Wordmark } from "@/components/Navbar";

const inputCls =
  "w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none transition-colors focus:border-orange focus:ring-2 focus:ring-orange/20";

const emptyProject = {
  title: "", category: "original", type: "Feature", year: new Date().getFullYear(),
  client: "", director: "", tagline: "", synopsis: "", director_note: "",
  poster: "", stillsText: "", trailer_url: "", imdb_url: "", creditsText: "", awardsText: "",
  featured: false, in_development: false, published: true,
};

const emptyPost = {
  title: "", category: "Studio", excerpt: "", contentText: "", image: "", author: "Incolour Films", published: true,
};

function projectToForm(p) {
  return {
    ...p,
    in_development: !!p.in_development,
    client: p.client || "",
    director: p.director || "",
    tagline: p.tagline || "",
    director_note: p.director_note || "",
    trailer_url: p.trailer_url || "",
    imdb_url: p.imdb_url || "",
    stillsText: (p.stills || []).join("\n"),
    creditsText: (p.credits || []).map((c) => `${c.role} | ${c.name}`).join("\n"),
    awardsText: (p.awards || []).join("\n"),
  };
}

function formToProject(f) {
  return {
    title: f.title,
    category: f.category,
    type: f.type,
    year: Number(f.year) || new Date().getFullYear(),
    client: f.client || null,
    director: f.director || null,
    tagline: f.tagline || null,
    synopsis: f.synopsis,
    director_note: f.director_note || null,
    poster: f.poster,
    stills: f.stillsText.split("\n").map((s) => s.trim()).filter(Boolean),
    trailer_url: f.trailer_url || null,
    imdb_url: f.imdb_url || null,
    credits: f.creditsText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [role, ...rest] = l.split("|");
      return { role: role.trim(), name: rest.join("|").trim() };
    }).filter((c) => c.role && c.name),
    awards: f.awardsText.split("\n").map((s) => s.trim()).filter(Boolean),
    featured: !!f.featured,
    in_development: !!f.in_development,
    published: !!f.published,
  };
}

function postToForm(p) {
  return { ...p, contentText: (p.content || []).join("\n\n") };
}

function formToPost(f) {
  return {
    title: f.title,
    category: f.category,
    excerpt: f.excerpt,
    content: f.contentText.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
    image: f.image,
    author: f.author,
    published: !!f.published,
  };
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function ProjectForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formToProject(f);
      if (f.id) await api.put(`/admin/projects/${f.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success(f.id ? "Project updated" : "Project created");
      onSave();
    } catch (err) {
      toast.error("Save failed — check required fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="cms-project-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title *"><input required value={f.title} onChange={set("title")} className={inputCls} data-testid="cms-project-title-input" /></Field>
        <Field label="Category">
          <select value={f.category} onChange={set("category")} className={inputCls} data-testid="cms-project-category-select">
            <option value="original">Original Work</option>
            <option value="service">Service Production</option>
          </select>
        </Field>
        <Field label="Format">
          <select value={f.type} onChange={set("type")} className={inputCls}>
            {["Feature", "Documentary", "Short", "Commercial", "PSA", "Music Video", "Series", "Other"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Year"><input type="number" value={f.year} onChange={set("year")} className={inputCls} /></Field>
        <Field label="Client (service work)"><input value={f.client} onChange={set("client")} className={inputCls} /></Field>
        <Field label="Director"><input value={f.director} onChange={set("director")} className={inputCls} /></Field>
      </div>
      <Field label="Tagline"><input value={f.tagline} onChange={set("tagline")} className={inputCls} /></Field>
      <Field label="Synopsis *"><textarea required rows={4} value={f.synopsis} onChange={set("synopsis")} className={inputCls} /></Field>
      <Field label="Director's note"><textarea rows={2} value={f.director_note} onChange={set("director_note")} className={inputCls} /></Field>
      <Field label="Poster image URL *"><input required value={f.poster} onChange={set("poster")} className={inputCls} placeholder="https://…" data-testid="cms-project-poster-input" /></Field>
      <Field label="Stills URLs — one per line"><textarea rows={3} value={f.stillsText} onChange={set("stillsText")} className={inputCls} /></Field>
      <Field label="Trailer URL (YouTube or Vimeo)"><input value={f.trailer_url} onChange={set("trailer_url")} className={inputCls} placeholder="https://www.youtube.com/watch?v=…" /></Field>
      <Field label="IMDb URL (optional)"><input value={f.imdb_url} onChange={set("imdb_url")} className={inputCls} placeholder="https://www.imdb.com/title/tt…" data-testid="cms-project-imdb-input" /></Field>
      <Field label="Credits — one per line, e.g. Director | Jane Doe or Starring | Jane Doe"><textarea rows={4} value={f.creditsText} onChange={set("creditsText")} className={inputCls} /></Field>
      <Field label="Awards — one per line"><textarea rows={2} value={f.awardsText} onChange={set("awardsText")} className={inputCls} /></Field>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={set("featured")} className="h-4 w-4 accent-[#FF5A1F]" data-testid="cms-project-featured-check" /> Featured on homepage</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.in_development} onChange={set("in_development")} className="h-4 w-4 accent-[#FF5A1F]" data-testid="cms-project-dev-check" /> In development (listed separately, not in the grid)</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} className="h-4 w-4 accent-[#FF5A1F]" /> Published</label>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="rounded-full bg-orange px-6 py-3 text-sm font-semibold text-cream disabled:opacity-60" data-testid="cms-project-save-btn">
          {saving ? "Saving…" : "Save project"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-border px-6 py-3 text-sm font-semibold">Cancel</button>
      </div>
    </form>
  );
}

function PostForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formToPost(f);
      if (f.id) await api.put(`/admin/journal/${f.id}`, payload);
      else await api.post("/admin/journal", payload);
      toast.success(f.id ? "Article updated" : "Article created");
      onSave();
    } catch {
      toast.error("Save failed — check required fields");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="cms-journal-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title *"><input required value={f.title} onChange={set("title")} className={inputCls} data-testid="cms-journal-title-input" /></Field>
        <Field label="Category">
          <select value={f.category} onChange={set("category")} className={inputCls}>
            {["Festival", "Studio", "Behind the Scenes", "Release", "Community"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Author"><input value={f.author} onChange={set("author")} className={inputCls} /></Field>
        <Field label="Hero image URL *"><input required value={f.image} onChange={set("image")} className={inputCls} placeholder="https://…" /></Field>
      </div>
      <Field label="Excerpt"><textarea rows={2} value={f.excerpt} onChange={set("excerpt")} className={inputCls} /></Field>
      <Field label="Body — separate paragraphs with a blank line"><textarea required rows={8} value={f.contentText} onChange={set("contentText")} className={inputCls} data-testid="cms-journal-content-input" /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} className="h-4 w-4 accent-[#FF5A1F]" /> Published</label>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="rounded-full bg-orange px-6 py-3 text-sm font-semibold text-cream disabled:opacity-60" data-testid="cms-journal-save-btn">
          {saving ? "Saving…" : "Save article"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-border px-6 py-3 text-sm font-semibold">Cancel</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  usePageTitle("CMS");
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [editing, setEditing] = useState(null); // {kind:'project'|'post', data}

  const loadProjects = useCallback(() => api.get("/admin/projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : (r.data?.projects || []))).catch(() => setProjects([])), []);
  const loadPosts = useCallback(() => api.get("/admin/journal").then((r) => setPosts(Array.isArray(r.data) ? r.data : (r.data?.posts || r.data?.journal || []))).catch(() => setPosts([])), []);

  useEffect(() => {
    const token = localStorage.getItem("incolour_token");
    if (!token) return;
    loadProjects();
    loadPosts();
  }, [user, loadProjects, loadPosts]);

  useEffect(() => {
    const token = localStorage.getItem("incolour_token");
    if (token && tab === "inquiries") {
      api.get("/admin/inquiries")
        .then((r) => setInquiries(Array.isArray(r.data) ? r.data : (r.data?.inquiries || [])))
        .catch(() => setInquiries([]));
    }
  }, [tab, user]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const delProject = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/projects/${p.id}`);
    toast.success("Project deleted");
    loadProjects();
  };

  const delPost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/admin/journal/${p.id}`);
    toast.success("Article deleted");
    loadPosts();
  };

  const projectList = Array.isArray(projects) ? projects : [];
  const postList = Array.isArray(posts) ? posts : [];
  const inquiryList = Array.isArray(inquiries) ? inquiries : [];

  const TABS = [
    { k: "projects", label: "Projects", icon: Film, count: projectList.length },
    { k: "journal", label: "Journal", icon: Newspaper, count: postList.length },
    { k: "inquiries", label: "Inquiries", icon: Inbox, count: null },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="cms-dashboard">
      <SEO title="CMS" noindex />
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/"><Wordmark /></Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-orange" data-testid="cms-view-site-link">
              <ExternalLink size={14} /> View site
            </Link>
            <span className="hidden text-sm text-muted-foreground sm:block">{user?.email || "Admin"}</span>
            <button onClick={logout} className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-coral hover:text-coral" data-testid="cms-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button
                key={t.k}
                onClick={() => { setTab(t.k); setEditing(null); }}
                data-testid={`cms-tab-${t.k}`}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                  tab === t.k ? "border-ink bg-ink text-cream dark:border-cream dark:bg-cream dark:text-ink" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <t.icon size={15} /> {t.label}{t.count != null && <span className="text-orange"> {t.count}</span>}
              </button>
            ))}
          </div>
          {tab === "projects" && (
            <button onClick={() => setEditing({ kind: "project", data: { ...emptyProject } })} data-testid="cms-add-project-btn" className="flex items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-cream">
              <Plus size={15} /> Add project
            </button>
          )}
          {tab === "journal" && (
            <button onClick={() => setEditing({ kind: "post", data: { ...emptyPost } })} data-testid="cms-add-post-btn" className="flex items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-semibold text-cream">
              <Plus size={15} /> Add article
            </button>
          )}
        </div>

        {editing && (
          <div className="mt-8 rounded-2xl border border-orange/40 bg-cream2 p-6 dark:bg-card sm:p-8" data-testid="cms-editor-panel">
            <h2 className="mb-6 font-display text-xl font-bold">
              {editing.kind === "project" ? (editing.data.id ? "Edit project" : "New project") : editing.data.id ? "Edit article" : "New article"}
            </h2>
            {editing.kind === "project" ? (
              <ProjectForm initial={editing.data} onSave={() => { setEditing(null); loadProjects(); }} onCancel={() => setEditing(null)} />
            ) : (
              <PostForm initial={editing.data} onSave={() => { setEditing(null); loadPosts(); }} onCancel={() => setEditing(null)} />
            )}
          </div>
        )}

        {tab === "projects" && (
          <div className="mt-8 divide-y divide-border overflow-hidden rounded-2xl border border-border" data-testid="cms-projects-list">
            {projectList.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-background p-4">
                <img src={p.poster} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display font-bold">{p.title}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">
                    <span className={p.category === "original" ? "text-orange" : "text-blue"}>{p.category}</span> · {p.type} · {p.year}
                    {p.featured && <span className="text-orange"> · Featured</span>}
                    {!p.published && <span className="text-coral"> · Draft</span>}
                  </p>
                </div>
                <button onClick={() => setEditing({ kind: "project", data: projectToForm(p) })} className="rounded-full border border-border p-2.5 hover:border-orange hover:text-orange" aria-label={`Edit ${p.title}`} data-testid={`cms-edit-project-${p.slug}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => delProject(p)} className="rounded-full border border-border p-2.5 hover:border-coral hover:text-coral" aria-label={`Delete ${p.title}`} data-testid={`cms-delete-project-${p.slug}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {projects.length === 0 && <p className="p-8 text-center text-muted-foreground">No projects yet.</p>}
          </div>
        )}

        {tab === "journal" && (
          <div className="mt-8 divide-y divide-border overflow-hidden rounded-2xl border border-border" data-testid="cms-journal-list">
            {postList.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-background p-4">
                <img src={p.image} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display font-bold">{p.title}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">
                    {p.category} · {formatDate(p.published_at)}{!p.published && <span className="text-coral"> · Draft</span>}
                  </p>
                </div>
                <button onClick={() => setEditing({ kind: "post", data: postToForm(p) })} className="rounded-full border border-border p-2.5 hover:border-orange hover:text-orange" aria-label={`Edit ${p.title}`} data-testid={`cms-edit-post-${p.slug}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => delPost(p)} className="rounded-full border border-border p-2.5 hover:border-coral hover:text-coral" aria-label={`Delete ${p.title}`} data-testid={`cms-delete-post-${p.slug}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {posts.length === 0 && <p className="p-8 text-center text-muted-foreground">No articles yet.</p>}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="mt-8 space-y-4" data-testid="cms-inquiries-list">
            {inquiryList.map((q) => (
              <div key={q.id} className="rounded-2xl border border-border bg-background p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display font-bold">{q.name} <span className="text-sm font-normal text-muted-foreground">· {q.email}{q.company ? ` · ${q.company}` : ""}</span></p>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{formatDate(q.created_at)}</p>
                </div>
                <p className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-orange">{[q.project_type, q.budget].filter(Boolean).join(" · ") || "General inquiry"}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{q.message}</p>
              </div>
            ))}
            {inquiries.length === 0 && <p className="rounded-2xl border border-border p-8 text-center text-muted-foreground">No inquiries yet — they'll land here when the contact form is used.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
